import { fetchWall } from "../lib/cloud";
import { createVisibilityInterval, type VisibilityIntervalHandle } from "../lib/visibility-poll";
import { clearWallCountdownTimers, renderWallBlocks } from "./wall/render-blocks";
import type { WallBlock, WallLiveData } from "./wall/types";

let navigateHandler: ((section: string) => void) | null = null;
let authenticated = false;
let pollHandle: VisibilityIntervalHandle | null = null;

export function setWallNavigateHandler(fn: (section: string) => void): void {
  navigateHandler = fn;
}

export function setWallAuthenticated(on: boolean): void {
  authenticated = on;
}

export function getWallScreenEl(): HTMLElement | null {
  return document.getElementById("wallScreen");
}

async function loadAndRender(
  container: HTMLElement,
  mode: "view" | "preview" = "view",
  opts?: { silent?: boolean },
): Promise<void> {
  const silent = !!opts?.silent;
  if (!silent) container.classList.add("wallBlocks--loading");
  try {
    const data = await fetchWall();
    const blocks = (data.blocks ?? []) as WallBlock[];
    const live = (data.live ?? null) as WallLiveData | null;
    renderWallBlocks(blocks, {
      mode,
      live,
      authenticated,
      onNavigate: (section) => navigateHandler?.(section),
      onPollVoted: () => void loadAndRender(container, mode),
      onCounterUpdate: (blockId, value) => {
        if (live?.counters) live.counters[blockId] = value;
      },
    }, container);
    if (mode === "view") container.classList.add("wallBlocks--settled");
  } catch {
    container.replaceChildren();
    const err = document.createElement("p");
    err.className = "accessHint";
    err.textContent = "Could not load Wall content.";
    container.appendChild(err);
  } finally {
    container.classList.remove("wallBlocks--loading");
  }
}

export function refreshWallScreen(): void {
  const mount = document.getElementById("wallBlocksMount");
  if (mount && !document.getElementById("wallScreen")?.classList.contains("hidden")) {
    void loadAndRender(mount, "view");
  }
}

export function openWallScreen(): void {
  const screen = getWallScreenEl();
  const mount = document.getElementById("wallBlocksMount");
  if (!screen || !mount) return;
  pollHandle?.stop();
  pollHandle = null;
  void loadAndRender(mount, "view");
  pollHandle = createVisibilityInterval(() => {
    if (!screen.classList.contains("hidden")) void loadAndRender(mount, "view", { silent: true });
  }, 15000);
}

export function closeWallScreen(): void {
  pollHandle?.stop();
  pollHandle = null;
  clearWallCountdownTimers();
}

export function mountWallScreen(): void {
  /* Native scroll on #wallScreenScroll (same as Settings). */
}
